import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import TopicCard from "@/components/feature/TopicCard";
import Reveal from "@/components/feature/Reveal";
import { categories, siteMeta } from "@/mocks/site";
import { topics } from "@/mocks/topics";
import TopicsToolbar, { type SortKey } from "./components/TopicsToolbar";

const origin = "https://youhuochigua.example";

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCat = searchParams.get("cat") ?? "全部";

  const [query, setQuery] = useState(searchParams.get("q") ?? "");
  const [category, setCategory] = useState(
    categories.some((item) => item.key === initialCat) ? initialCat : "全部",
  );
  const [sort, setSort] = useState<SortKey>("heat");

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { __all: topics.length };
    topics.forEach((topic) => {
      counts[topic.category] = (counts[topic.category] ?? 0) + 1;
    });
    return counts;
  }, []);

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    const result = topics.filter((topic) => {
      const matchCategory = category === "全部" || topic.category === category;
      const matchKeyword =
        keyword.length === 0 ||
        topic.title.toLowerCase().includes(keyword) ||
        topic.summary.toLowerCase().includes(keyword) ||
        topic.category.toLowerCase().includes(keyword) ||
        topic.tags.some((tag) => tag.toLowerCase().includes(keyword));
      return matchCategory && matchKeyword;
    });

    return result.sort((a, b) =>
      sort === "heat"
        ? b.heat - a.heat
        : new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  }, [query, category, sort]);

  const handleCategoryChange = (value: string) => {
    setCategory(value);
    const next = new URLSearchParams(searchParams);
    if (value === "全部") {
      next.delete("cat");
    } else {
      next.set("cat", value);
    }
    setSearchParams(next, { replace: true });
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    const next = new URLSearchParams(searchParams);
    if (value.trim().length === 0) {
      next.delete("q");
    } else {
      next.set("q", value);
    }
    setSearchParams(next, { replace: true });
  };

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "事件复盘列表 - 诱惑吃瓜",
      description:
        "按分类与热度浏览诱惑吃瓜的全部事件复盘，包含实锤、反转、辟谣与待跟进四类结论的事件整理。",
      url: `${origin}/topics`,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "诱惑吃瓜 事件复盘",
      numberOfItems: topics.length,
      itemListElement: topics.map((topic, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `${origin}/topics/${topic.id}`,
        name: topic.title,
      })),
    },
  ];

  return (
    <>
      <Seo
        title="事件复盘列表 - 诱惑吃瓜 实锤与反转全收录"
        description="按分类、热度与关键词浏览诱惑吃瓜的全部事件复盘。每篇都包含完整时间线、证据清单与双方说法，涵盖实锤、反转、辟谣与待跟进四类结论。"
        keywords="诱惑吃瓜,事件复盘,吃瓜实锤,吃瓜反转,吃瓜辟谣,来龙去脉"
        path="/topics"
        jsonLd={jsonLd}
      />
      <SiteHeader />
      <main className="bg-background-100">
        <header className="border-b border-background-200 bg-background-50 px-4 pb-8 pt-28 md:px-8 md:pb-10 md:pt-32 lg:px-12">
          <div className="mx-auto max-w-6xl">
            <Breadcrumbs items={[{ label: "首页", path: "/" }, { label: "事件复盘" }]} />
            <h1 className="mt-4 font-heading text-2xl font-bold leading-tight tracking-tight text-foreground-950 md:text-4xl">
              事件复盘列表
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              共收录 {topics.length} 个已整理事件。每个事件都按时间顺序梳理来龙去脉，标注材料性质与结论状态，方便你快速判断该看哪一篇。
            </p>
          </div>
        </header>

        <div className="px-4 py-10 md:px-8 md:py-14 lg:px-12">
          <div className="mx-auto max-w-6xl">
            <TopicsToolbar
              query={query}
              onQueryChange={handleQueryChange}
              category={category}
              onCategoryChange={handleCategoryChange}
              sort={sort}
              onSortChange={setSort}
              resultCount={filtered.length}
              categoryCounts={categoryCounts}
            />

            {filtered.length > 0 ? (
              <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((topic, index) => (
                  <Reveal key={topic.id} delay={index * 50}>
                    <TopicCard topic={topic} />
                  </Reveal>
                ))}
              </div>
            ) : (
              <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-2xl text-primary-600">
                  <i className="ri-ghost-line" aria-hidden="true"></i>
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  没有匹配的事件
                </h2>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
                  换一个关键词，或者把分类切回「全部」试试。如果你有关注的事件还没被整理，欢迎在联系我们页面告诉我们。
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setQuery("");
                    handleCategoryChange("全部");
                  }}
                  className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  重置筛选条件
                </button>
              </div>
            )}
          </div>
        </div>

        <section className="border-t border-background-200 bg-background-50 px-4 py-12 md:px-8 lg:px-12">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              没找到你想看的事件？
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              {siteMeta.name}每天跟进新增线索。如果某个事件迟迟没有复盘，很可能是材料还不足以支撑结论，你可以把来源线索发给我们，加速整理进度。
            </p>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}